import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Trash2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Logo } from "@/components/Logo";
import { Field, Panel, useAdminSettings } from "@/components/admin/shared";
import { EmailAlertPanel } from "@/components/admin/EmailAlertPanel";
import { PasswordPanel } from "@/components/admin/PasswordPanel";

const LogoUploader = () => {
  const { site, reload } = useSite();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post("/admin/logo", fd); await reload(); toast.success("Logo actualizado"); } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };
  const remove = async () => { await api.delete("/admin/logo"); await reload(); toast.success("Se restauró el logo provisional"); };
  return (
    <Panel title="Logo original">
      <div className="flex flex-wrap items-center gap-6">
        <div className="rounded-md border border-dashed border-brand/40 bg-surface p-6" data-testid="logo-preview"><Logo size={72} /></div>
        <div className="space-y-3">
          <p className="text-sm text-mute">Sube tu logo en PNG, JPG, WEBP o SVG (máx. 5 MB). Se mostrará en todo el sitio.</p>
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="logo-file-input" />
          <div className="flex gap-3">
            <button className="btn-gold !py-2" disabled={busy} onClick={() => ref.current.click()} data-testid="logo-upload-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Subir logo</button>
            {site?.has_logo && <button className="btn-ghost !py-2" onClick={remove} data-testid="logo-remove-btn"><Trash2 size={16} /> Quitar</button>}
          </div>
        </div>
      </div>
    </Panel>
  );
};

const Form = ({ settings, save }) => {
  const [b, setB] = useState(settings.business);
  const [s, setS] = useState(settings.social);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel title="Datos del negocio">
        <div className="space-y-4">
          <Field id="biz-address" label="Dirección" value={b.address} onChange={(e) => setB({ ...b, address: e.target.value })} />
          <Field id="biz-phone" label="Teléfono visible" value={b.phone} onChange={(e) => setB({ ...b, phone: e.target.value })} />
          <Field id="biz-whatsapp" label="WhatsApp (con 52, solo dígitos)" value={b.whatsapp} onChange={(e) => setB({ ...b, whatsapp: e.target.value.replace(/\D/g, "") })} />
          <Field id="biz-email" label="Correo (opcional)" type="email" value={b.email} onChange={(e) => setB({ ...b, email: e.target.value })} />
          <Field id="biz-review-url" label="Enlace para opinar en Google" value={b.google_review_url} onChange={(e) => setB({ ...b, google_review_url: e.target.value })} />
          <Field id="biz-maps-url" label="Enlace para leer opiniones (Google Maps)" value={b.google_maps_url} onChange={(e) => setB({ ...b, google_maps_url: e.target.value })} />
        </div>
      </Panel>
      <Panel title="Redes sociales">
        <p className="mb-4 text-sm text-mute">Facebook se muestra como muro que se actualiza solo. TikTok muestra tus videos más recientes. Instagram y YouTube aparecen como botón a tu perfil (sus plataformas no permiten un muro automático sin una app aprobada).</p>
        <div className="space-y-4">
          <Field id="social-facebook" label="Facebook (URL de la página)" placeholder="https://www.facebook.com/tu-pagina" value={s.facebook} onChange={(e) => setS({ ...s, facebook: e.target.value })} />
          <Field id="social-instagram" label="Instagram" placeholder="https://www.instagram.com/tu-cuenta" value={s.instagram} onChange={(e) => setS({ ...s, instagram: e.target.value })} />
          <Field id="social-tiktok" label="TikTok" placeholder="https://www.tiktok.com/@tu-cuenta" value={s.tiktok} onChange={(e) => setS({ ...s, tiktok: e.target.value })} />
          <Field id="social-youtube" label="YouTube" placeholder="https://www.youtube.com/@tu-canal" value={s.youtube} onChange={(e) => setS({ ...s, youtube: e.target.value })} />
        </div>
      </Panel>
      <div className="lg:col-span-2"><button className="btn-gold" onClick={() => save({ business: b, social: s })} data-testid="site-settings-save-btn">Guardar datos del sitio</button></div>
    </div>
  );
};

export const SettingsTab = () => {
  const { settings, save } = useAdminSettings();
  return (
    <div className="space-y-6">
      {settings && <EmailAlertPanel settings={settings} save={save} />}
      <LogoUploader />
      {settings ? <Form settings={settings} save={save} /> : <Loader2 className="animate-spin text-brand" />}
      <PasswordPanel />
    </div>
  );
};
